import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useFormik } from "formik";
import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { connect, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import * as yup from "yup";
import { appName, mainBg } from "../../config/constants";
import { login } from "../../store/actions/auth";

import { useTranslation } from "react-i18next";
import { HiLockClosed } from "react-icons/hi";
import { ImPhone } from "react-icons/im";
import CountrySelect from "../../components/CountrySelect";

const validationSchema = yup.object({
  mobile: yup
    .string("Enter your mobile")
    .max(15, "too long")
    .matches(
      /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/,
      "Must be a valid phone number ex: 1124356930"
    )
    .required("Mobile Number is required"),
  password: yup.string().min(4).required("Password is required"),
  countryCode: yup.string().max(20).required("+20"),
});

export const Login = ({ isAuthenticated, login, authError }) => {
  const logoIcon = useSelector((s) => s.app.logo);
  const companyId = useSelector((s) => s.app.company)?.id;
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation(["auth"]);
  let navigate = useNavigate();
  let location = useLocation();

  const currentLocationForRedirect =
    location?.state?.currentLocationForRedirect;

  const from = location.state?.from || "/";

  let pageId = location?.state?.pageId;

  useEffect(() => {
    if (isAuthenticated) {
      const invitationKey = localStorage.getItem("invitationKey");
      if (invitationKey) {
        return navigate(`${invitationKey}`);
      }
      navigate(from, { replace: true });
      if (
        currentLocationForRedirect &&
        !String(currentLocationForRedirect)?.includes("login")
      ) {
        return navigate(currentLocationForRedirect);
      }
      return navigate("/");
    }
  }, [isAuthenticated, navigate, from, currentLocationForRedirect]);

  const formik = useFormik({
    initialValues: {
      mobile: "",
      password: "",
      countryCode: "20",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      const { mobile, password, countryCode } = values;
      try {
        await login({
          mobile:
            countryCode + (mobile.startsWith("0") ? mobile.slice(1) : mobile),
          password,
          pageId,
          companyId,
          cb: (notVerified) =>
            notVerified &&
            navigate(`/verify-account?identifier=${countryCode + mobile}`),
        });
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    },
  });

  const navToForgetPassword = () => navigate("/reset-password");
  const chooseCountryCode = useCallback(
    (code) => formik.setFieldValue("countryCode", code),
    [formik]
  );

  return (
    <Grid
      container
      sx={{
        height: 1,
        backgroundImage: `url(${mainBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        minHeight: "100dvh",
      }}
      display="flex"
      alignItems="center"
      justifyContent="flex-end"
      alignContent="center"
    >
      <Helmet>
        <title>{appName} | Login</title>
        <meta name="description" content={`${appName} login page`} />
        <meta property="og:title" content={`${appName} | Login`} />
        <meta property="og:description" content={`${appName} | Login`} />
        <meta property="og:image" content={logoIcon} />
        <meta property="og:url" content={`${appName} | Login`} />
        <link rel="canonical" href={`${appName} | Login`} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={`${appName} Online paltform`} />
        <meta property="og:image:alt" content={`${appName} | Login`} />
        <meta property="fb:app_id" content="559076901495442" />
        <meta name="twitter:title" content={`${appName} | Login`} />
        <meta name="twitter:description" content={`${appName} | Login`} />
        <meta name="twitter:image" content={logoIcon} />
        <meta name="twitter:card" content="summary_large_image" />

        <link rel="preload" as="image" href={logoIcon} />
      </Helmet>
      <Grid
        item
        md={6}
        sm={12}
        xs={12}
        alignContent="start"
        container
        justifyContent="start"
        textAlign="start"
      >
        <Grid
          item
          justifyContent="start"
          alignSelf="start"
          justifySelf="start"
          textAlign="start"
          sx={{ display: { xs: "none", sm: "none", md: "block" }, ml: 10 }}
        >
          <img
            loading="lazy"
            src={"/images/layout/logo.png"}
            height={100}
            alt=""
          />
        </Grid>
      </Grid>
      <Grid
        item
        md={6}
        sm={12}
        xs={12}
        sx={{ px: { md: 5, xs: 1, sm: 1 }, height: 1 }}
        alignContent="center"
        container
      >
        <Grid
          item
          sm={12}
          xs={12}
          sx={{ height: { lg: "65%", md: "80%", sm: "80%" } }}
        >
          <Paper
            elevation={1}
            sx={{
              py: 2,
              px: 3,
              borderRadius: "24px",
              textAlign: "center",
              alignSelf: "center",
              width: "100%",
              backgroundColor: "background.authBg",
              maxWidth: "712px",
            }}
          >
            <img src={logoIcon} alt="pyxiz" height={72} />
            <form
              onSubmit={formik.handleSubmit}
              style={{ height: "100%", width: "100%" }}
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                justifyContent="space-between"
                height={1}
                width={1}
                sx={{ height: "100%", width: "100%" }}
              >
                <Box>
                  <Typography variant="h5" color="text" fontWeight="bold">
                    <Box component="span" sx={{ color: "primary.main" }}>
                      {t("welcome")}
                    </Box>
                    {t("welcome_back")}!
                  </Typography>
                </Box>
                <Box width={1}>
                  <Box display="flex" flexDirection="column" sx={{}}>
                    <Box
                      sx={{
                        width: "100%",
                        my: 2,
                        backgroundColor: "background.paper",
                        borderRadius: "8px",
                        alignItems: "center",
                      }}
                      display="flex"
                    >
                      <Box
                        sx={{
                          px: 2,
                          borderRight: "2px solid darkgrey",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "text.secondary",
                        }}
                      >
                        <ImPhone size={20} />
                      </Box>
                      <Box
                        sx={{
                          width: {
                            xl: "20%",
                            lg: "20%",
                            md: "30%",
                            sm: "30%",
                            xs: "30%",
                          },
                          mr: 1,
                        }}
                      >
                        <CountrySelect
                          chooseCode={chooseCountryCode}
                          error={
                            formik.touched.countryCode &&
                            Boolean(formik.errors.countryCode)
                          }
                          helperText={
                            formik.touched.countryCode &&
                            formik.errors.countryCode
                          }
                          disableBorder
                        />
                      </Box>
                      <TextField
                        fullWidth
                        id="mobile"
                        name="mobile"
                        value={formik.values.mobile}
                        onChange={formik.handleChange}
                        error={
                          (formik.touched.mobile &&
                            Boolean(formik.errors.mobile)) ||
                          authError
                        }
                        helperText={
                          formik.touched.mobile &&
                          (formik.errors.mobile || authError)
                        }
                        placeholder={t("data.mobile")}
                        variant="standard"
                        InputProps={{
                          disableUnderline: true,
                        }}
                      />
                    </Box>

                    <Box
                      sx={{
                        width: "100%",
                        my: 2,
                        mt: authError && { xs: 3, sm: 0 },
                      }}
                    >
                      <TextField
                        fullWidth
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.password &&
                          Boolean(formik.errors.password)
                        }
                        helperText={
                          formik.touched.password &&
                          (formik.errors.password || authError)
                        }
                        id="password"
                        name="password"
                        type="Password"
                        variant="standard"
                        InputProps={{
                          sx: { minHeight: "56px" },
                          disableUnderline: true,
                          startAdornment: (
                            <InputAdornment
                              sx={{ color: "text.secondary" }}
                              position="start"
                            >
                              <HiLockClosed size={25} />
                            </InputAdornment>
                          ),
                        }}
                        placeholder={t("data.password")}
                        sx={{
                          backgroundColor: "background.paper",
                          borderRadius: "8px",
                          minHeight: "56px",
                          display: "flex",
                          alignItems: "center",
                          px: 2,
                        }}
                      />
                      <Box textAlign="end" width={1} sx={{ mt: 1 }}>
                        <Typography
                          className="pointer"
                          onClick={navToForgetPassword}
                          sx={{
                            mt: authError && { xs: 2, sm: 0 },
                            textDecoration: "underline",
                          }}
                        >
                          {t("forgot_password")}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Box width={1}>
                  <Button
                    fullWidth
                    color="secondary"
                    type="submit"
                    variant="contained"
                    sx={{ py: 1.5, fontWeight: "bold" }}
                    disabled={loading}
                  >
                    {t("login")}
                  </Button>
                </Box>

                <Box
                  width={1}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pt: 2,
                  }}
                >
                  <Typography sx={{ mr: 1 }}>
                    {t("dont_have_account")}
                  </Typography>

                  <Typography
                    className="pointer"
                    onClick={() =>
                      navigate("/register", {
                        state: { from, pageId },
                      })
                    }
                    color="primary"
                    fontWeight="bold"
                  >
                    {t("register_now")}
                  </Typography>
                </Box>
              </Box>
            </form>
          </Paper>
        </Grid>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = (state) => ({
  isAuthenticated: state?.auth?.isAuthenticated,
  authError: state?.auth?.error,
});

const mapDispatchToProps = { login };

export default connect(mapStateToProps, mapDispatchToProps)(Login);
